import { useState, useRef } from "react";

// ─── Seed Data ────────────────────────────────────────────────────────────────
const SEED = {
  program: {
    name: "Oracle Fusion Enterprise Data Warehouse and AI Program",
    source_system: "Oracle Fusion 25B+",
    etl_partner: "Orbit Analytics",
    methodology: "Agile Hybrid Enterprise Deliavery",
    delivery_model: "Time and Materials",
    duration_months: 12,
    status: "Planned",
  },
  phases: [
    {
      id: "P1", name: "Infrastructure and Governance Foundation",
      start_month: 1, end_month: 3, status: "Not Started", color: "#2B5C8A",
      objectives: [
        "Deploy enterprise cloud infrastructure",
        "Implement governance and security",
        "Deploy ETL integration framework",
        "Establish enterprise metadata management",
        "Create enterprise dimensional standards",
      ],
      deliverables: [
        "Enterprise data platform",
        "Enterprise governance framework",
        "ETL integration framework",
        "Shared dimensional model",
        "Security and audit framework",
        "Monitoring and logging framework",
      ],
    },
    {
      id: "P2", name: "Financial Data Warehouse",
      start_month: 2, end_month: 7, status: "Not Started", color: "#1a7a4a",
      objectives: [
        "Build enterprise financial dimensional warehouse",
        "Deploy SOX-compliant financial reporting",
        "Enable enterprise financial analytics",
        "Standardize financial KPIs",
      ],
      deliverables: [
        "100 Financial Dashboards",
        "Enterprise Financial Semantic Layer",
        "SOX Reporting Framework",
        "Daily Close Dashboard",
        "Financial KPI Catalog",
        "Enterprise Financial Data Mart",
      ],
    },
    {
      id: "P3", name: "Supply Chain Data Warehouse",
      start_month: 3, end_month: 7, status: "Not Started", color: "#7A5C1E",
      objectives: [
        "Deliver enterprise operational analytics",
        "Enable manufacturing visibility",
        "Improve supply chain forecasting",
        "Standardize operational KPIs",
      ],
      deliverables: [
        "50 SCM Dashboards",
        "Operational KPI Layer",
        "Manufacturing Analytics",
        "Inventory Optimization Analytics",
        "Supply Chain Executive Dashboard",
      ],
    },
    {
      id: "P4", name: "HR and Executive Analytics",
      start_month: 4, end_month: 8, status: "Not Started", color: "#6B2D8A",
      objectives: [
        "Deliver workforce analytics",
        "Provide executive operational visibility",
        "Enable workforce KPI governance",
      ],
      deliverables: [
        "25 HR Dashboards",
        "25 Executive Dashboards",
        "Enterprise KPI Heatmaps",
        "Board Reporting Package",
        "Executive Command Center",
      ],
    },
    {
      id: "P5", name: "Enterprise AI Data Layer",
      start_month: 4, end_month: 12, status: "Not Started", color: "#8B4500",
      objectives: [
        "Enable enterprise AI and ML",
        "Deploy predictive analytics",
        "Implement enterprise semantic AI",
        "Create AI-assisted forecasting",
      ],
      deliverables: [
        "Feature Store",
        "ML Pipelines",
        "Vector Database",
        "Enterprise LLM Layer",
        "AI Governance Framework",
        "Model Monitoring",
        "AI APIs — Finance, SCM, HR domains",
      ],
    },
  ],
  milestones: [
    { id: "M1",  phase_id: "P1", name: "Infrastructure Approved",            target_week: 2,  status: "Pending" },
    { id: "M2",  phase_id: "P1", name: "Environments Ready",                 target_week: 4,  status: "Pending" },
    { id: "M3",  phase_id: "P1", name: "Fusion Connectivity Complete",        target_week: 6,  status: "Pending" },
    { id: "M4",  phase_id: "P1", name: "Security & Governance Signoff",       target_week: 8,  status: "Pending" },
    { id: "M5",  phase_id: "P1", name: "ETL Framework Operational",           target_week: 10, status: "Pending" },
    { id: "M6",  phase_id: "P1", name: "Shared Dimensions Published",         target_week: 12, status: "Pending" },
    { id: "M7",  phase_id: "P2", name: "GL & AP Data Models Complete",        target_week: 10, status: "Pending" },
    { id: "M8",  phase_id: "P2", name: "Financial ETL Complete",              target_week: 16, status: "Pending" },
    { id: "M9",  phase_id: "P2", name: "SOX Dashboards Deployed",             target_week: 22, status: "Pending" },
    { id: "M10", phase_id: "P2", name: "100 Financial Dashboards UAT",        target_week: 26, status: "Pending" },
    { id: "M11", phase_id: "P2", name: "Financial Go-Live",                   target_week: 28, status: "Pending" },
    { id: "M12", phase_id: "P3", name: "Procurement & Inventory Models Done", target_week: 16, status: "Pending" },
    { id: "M13", phase_id: "P3", name: "Manufacturing Dashboards Live",       target_week: 22, status: "Pending" },
    { id: "M14", phase_id: "P3", name: "50 SCM Dashboards UAT",               target_week: 26, status: "Pending" },
    { id: "M15", phase_id: "P3", name: "SCM Go-Live",                         target_week: 28, status: "Pending" },
    { id: "M16", phase_id: "P4", name: "HCM Data Models Complete",            target_week: 20, status: "Pending" },
    { id: "M17", phase_id: "P4", name: "Executive Dashboards Draft",          target_week: 28, status: "Pending" },
    { id: "M18", phase_id: "P4", name: "Board Reporting Package Ready",       target_week: 30, status: "Pending" },
    { id: "M19", phase_id: "P4", name: "HR & Executive UAT Signoff",          target_week: 32, status: "Pending" },
    { id: "M20", phase_id: "P5", name: "AI Architecture Approved",            target_week: 18, status: "Pending" },
    { id: "M21", phase_id: "P5", name: "Feature Store Operational",           target_week: 24, status: "Pending" },
    { id: "M22", phase_id: "P5", name: "Financial AI Models in Production",   target_week: 30, status: "Pending" },
    { id: "M23", phase_id: "P5", name: "SCM & HR AI Models Live",             target_week: 36, status: "Pending" },
    { id: "M24", phase_id: "P5", name: "LLM & Semantic AI Integration",       target_week: 42, status: "Pending" },
    { id: "M25", phase_id: "P5", name: "AI CoE Transition & Stabilization",   target_week: 48, status: "Pending" },
  ],
  resources: [
    { id: "R1",  role: "Program Manager",      count: 1, phases: ["P1","P2","P3","P4","P5"], allocation: 100 },
    { id: "R2",  role: "Enterprise Architect",  count: 1, phases: ["P1","P2","P3","P4","P5"], allocation: 75  },
    { id: "R3",  role: "Finance Architect",     count: 1, phases: ["P2"],                     allocation: 100 },
    { id: "R4",  role: "SCM Architect",         count: 1, phases: ["P3"],                     allocation: 100 },
    { id: "R5",  role: "HCM Architect",         count: 1, phases: ["P4"],                     allocation: 100 },
    { id: "R6",  role: "AI Architect",          count: 1, phases: ["P5"],                     allocation: 100 },
    { id: "R7",  role: "ETL / Data Engineer",   count: 5, phases: ["P1","P2","P3","P4","P5"], allocation: 100 },
    { id: "R8",  role: "BI Developer",          count: 4, phases: ["P2","P3","P4"],           allocation: 100 },
    { id: "R9",  role: "Data Scientist / ML",   count: 2, phases: ["P5"],                     allocation: 100 },
    { id: "R10", role: "Cloud Engineer",        count: 2, phases: ["P1","P5"],                allocation: 100 },
    { id: "R11", role: "Security Lead",         count: 1, phases: ["P1","P2","P4"],           allocation: 75  },
    { id: "R12", role: "QA Lead",               count: 1, phases: ["P1","P2","P3","P4","P5"], allocation: 75  },
    { id: "R13", role: "Functional SME",        count: 8, phases: ["P2","P3","P4"],           allocation: 50  },
  ],
  risks: [
    { id: "K1", type: "Risk",       category: "Technical",      description: "Oracle Fusion 25B+ BICC extraction gaps for new modules", severity: "High",   status: "Open", mitigation: "Validate API coverage in Phase 1; build REST fallback pipelines" },
    { id: "K2", type: "Risk",       category: "Data Quality",   description: "Source data quality issues across 11 Fusion modules may delay AI model accuracy", severity: "High",   status: "Open", mitigation: "Implement automated DQ monitoring in Bronze layer from Phase 1" },
    { id: "K3", type: "Risk",       category: "Scope",          description: "200 dashboards + AI layer creates significant parallel workstream risk", severity: "High",   status: "Open", mitigation: "Dedicated delivery PODs per domain; biweekly steering reviews" },
    { id: "K4", type: "Risk",       category: "Resource",       description: "AI Architect and ML Engineer availability in competitive market", severity: "Medium", status: "Open", mitigation: "Lock resources before Phase 5 kickoff; consider staff augmentation" },
    { id: "K5", type: "Risk",       category: "Timeline",       description: "Overlapping phase starts (P2–P5 all begin by month 4) compress shared resource availability", severity: "Medium", status: "Open", mitigation: "POD structure isolates domain delivery; shared services tracked in RAID" },
    { id: "K6", type: "Assumption", category: "Licensing",      description: "Oracle Fusion 25B+ API and BICC licenses procured prior to Phase 1", severity: "Medium", status: "Open", mitigation: "Confirm with procurement in program kickoff week" },
    { id: "K7", type: "Assumption", category: "Infrastructure", description: "Cloud landing zone approved and provisioned within first 4 weeks", severity: "Medium", status: "Open", mitigation: "Parallel-track security approval with environment provisioning" },
    { id: "K8", type: "Assumption", category: "AI",             description: "Enterprise LLM and vector database platform selected before Phase 5 month 4 start", severity: "High",   status: "Open", mitigation: "Technology selection as Phase 1 deliverable" },
    { id: "K9", type: "Issue",      category: "Governance",     description: "AI governance and model risk framework not yet defined at enterprise level", severity: "High",   status: "Open", mitigation: "Establish AI governance workstream in Phase 1 alongside data governance" },
    { id: "K10", type: "Dependency", category: "Identity",      description: "Enterprise IdAM / SSO integration required for row-level security across all phases", severity: "Medium", status: "Open", mitigation: "Engage IdAM team in Week 1; gate Phase 2+ on SSO completion" },
    { id: "K11", type: "Dependency", category: "SME",           description: "Finance, SCM, and HR SMEs required concurrently from month 4 onward", severity: "Medium", status: "Open", mitigation: "Coordinate SME calendars; secure executive sponsorship for availability" },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 8).toUpperCase();
const toArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  return String(v).split("\n").map(s => s.trim()).filter(Boolean);
};
const PHASE_COLORS = { P1: "#2B5C8A", P2: "#1a7a4a", P3: "#7A5C1E", P4: "#6B2D8A", P5: "#8B4500" };
const STATUS_COLORS = {
  "Not Started": "#6B6B6B", "In Progress": "#2B5C8A", "Complete": "#1a7a4a",
  "Pending": "#6B6B6B", "Done": "#1a7a4a", "At Risk": "#8B0000",
  "Open": "#8B0000", "Closed": "#1a7a4a", "Mitigated": "#2B5C8A",
};
const SEVERITY_COLORS = { High: "#8B0000", Medium: "#7A5C1E", Low: "#2B5C8A" };

function Badge({ label, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", fontSize: "10px",
      fontFamily: "'DM Mono', monospace", fontWeight: 500, letterSpacing: "0.04em",
      border: `1px solid ${color || "#aaa"}`, color: color || "#aaa",
      borderRadius: "2px", textTransform: "uppercase",
    }}>{label}</span>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "16px",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#F7F5F0", width: "100%", maxWidth: "560px",
        maxHeight: "88vh", overflow: "auto", borderRadius: "3px",
        borderTop: "3px solid #2B5C8A",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px", borderBottom: "1px solid rgba(26,26,26,0.13)",
        }}>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "18px" }}>
            {title}
          </span>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: "20px", color: "#6B6B6B", lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <label style={{
        display: "block", marginBottom: "6px",
        fontFamily: "'DM Mono', monospace", fontSize: "11px",
        letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B6B6B",
      }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", fontSize: "14px",
  fontFamily: "'EB Garamond', serif", background: "#EFECE5",
  border: "1px solid rgba(26,26,26,0.2)", borderRadius: "2px",
  color: "#0A0A0A", outline: "none",
};
const selectStyle = { ...inputStyle };
const textareaStyle = { ...inputStyle, resize: "vertical", minHeight: "72px" };

function Btn({ onClick, variant = "primary", children, style: s }) {
  const base = {
    padding: "9px 18px", fontSize: "12px", fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer",
    border: "none", borderRadius: "2px", fontWeight: 500,
  };
  const variants = {
    primary: { background: "#2B5C8A", color: "#F7F5F0" },
    danger: { background: "#8B0000", color: "#F7F5F0" },
    ghost: { background: "transparent", color: "#2B5C8A", border: "1px solid rgba(43,92,138,0.4)" },
    gold: { background: "#7A5C1E", color: "#F7F5F0" },
  };
  return <button onClick={onClick} style={{ ...base, ...variants[variant], ...s }}>{children}</button>;
}

// ─── Section: Program Overview ────────────────────────────────────────────────
function ProgramSection({ data, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(data);

  const save = () => { onChange(draft); setEditing(false); };
  const fields = [
    ["name", "Program Name"], ["source_system", "Source System"],
    ["etl_partner", "ETL / Integration Partner"], ["methodology", "Methodology"],
    ["delivery_model", "Delivery Model"],
    ["duration_months", "Duration (Months)"], ["status", "Status"],
  ];

  return (
    <div>
      <SectionHeader title="Program Overview" action={<Btn onClick={() => setEditing(true)} variant="ghost">Edit</Btn>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1px", background: "rgba(26,26,26,0.1)", border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px", overflow: "hidden" }}>
        {fields.map(([k, label]) => (
          <div key={k} style={{ padding: "14px 16px", background: "#F7F5F0" }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#6B6B6B", marginBottom: "4px" }}>{label}</div>
            <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "15px", color: "#0A0A0A" }}>{data[k]}</div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title="Edit Program" onClose={() => setEditing(false)}>
          {fields.map(([k, label]) => (
            <Field key={k} label={label}>
              <input style={inputStyle} value={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.value })} />
            </Field>
          ))}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ title, action, sub }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: "16px", paddingBottom: "10px",
      borderBottom: "1px solid rgba(26,26,26,0.13)",
    }}>
      <div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "22px", color: "#0A0A0A" }}>{title}</h2>
        {sub && <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#6B6B6B", marginTop: "2px" }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Section: Timeline / Phases ───────────────────────────────────────────────
function PhasesSection({ phases, onChange }) {
  const [editTarget, setEditTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const blank = { id: "", name: "", start_month: 1, end_month: 3, status: "Not Started", color: "#2B5C8A", objectives: [], deliverables: [] };
  const [draft, setDraft] = useState(blank);

  const save = () => {
    const normalised = {
      ...draft,
      objectives: toArr(draft.objectives),
      deliverables: toArr(draft.deliverables),
    };
    if (editTarget) {
      onChange(phases.map(p => p.id === editTarget.id ? { ...editTarget, ...normalised } : p));
    } else {
      onChange([...phases, { ...normalised, id: "P" + uid() }]);
    }
    setEditTarget(null); setAddOpen(false); setDraft(blank);
  };

  const del = id => onChange(phases.filter(p => p.id !== id));

  const openEdit = p => { setDraft({ ...p, objectives: p.objectives.join("\n"), deliverables: p.deliverables.join("\n") }); setEditTarget(p); };
  const openAdd = () => { setDraft({ ...blank, objectives: "", deliverables: "" }); setAddOpen(true); };

  const TOTAL = 14;

  return (
    <div>
      <SectionHeader title="Program Phases" sub={`${phases.length} phases · ${TOTAL} month program`} action={<Btn onClick={openAdd}>+ Add Phase</Btn>} />

      {/* Gantt bar */}
      <div style={{ marginBottom: "24px", background: "#EFECE5", borderRadius: "3px", padding: "16px", border: "1px solid rgba(26,26,26,0.1)" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "10px" }}>Timeline — Months 1–{TOTAL}</div>
        <div style={{ position: "relative" }}>
          {/* Month ticks */}
          <div style={{ display: "flex", marginBottom: "6px" }}>
            {Array.from({ length: TOTAL }, (_, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#6B6B6B" }}>{i + 1}</div>
            ))}
          </div>
          {phases.map(p => {
            const left = ((p.start_month - 1) / TOTAL) * 100;
            const width = ((p.end_month - p.start_month + 1) / TOTAL) * 100;
            return (
              <div key={p.id} style={{ position: "relative", height: "32px", marginBottom: "6px" }}>
                <div style={{
                  position: "absolute", left: `${left}%`, width: `${width}%`,
                  height: "100%", background: p.color || "#2B5C8A",
                  borderRadius: "2px", display: "flex", alignItems: "center",
                  paddingLeft: "10px", overflow: "hidden",
                }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#fff", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>{p.id} — {p.name}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
        {phases.map(p => (
          <div key={p.id} style={{
            border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px",
            borderTop: `3px solid ${p.color || "#2B5C8A"}`, background: "#F7F5F0",
            padding: "16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", marginBottom: "4px", letterSpacing: "0.06em" }}>{p.id} · Months {p.start_month}–{p.end_month}</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "16px" }}>{p.name}</div>
              </div>
              <Badge label={p.status} color={STATUS_COLORS[p.status]} />
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Objectives</div>
            <ul style={{ paddingLeft: "14px", marginBottom: "12px" }}>
              {toArr(p.objectives).slice(0, 3).map((o, i) => (
                <li key={i} style={{ fontFamily: "'EB Garamond', serif", fontSize: "13px", marginBottom: "2px" }}>{o}</li>
              ))}
            </ul>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <Btn variant="ghost" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => openEdit(p)}>Edit</Btn>
              <Btn variant="danger" style={{ padding: "6px 12px", fontSize: "11px" }} onClick={() => del(p.id)}>Delete</Btn>
            </div>
          </div>
        ))}
      </div>

      {(editTarget || addOpen) && (
        <Modal title={editTarget ? `Edit ${editTarget.id}` : "Add Phase"} onClose={() => { setEditTarget(null); setAddOpen(false); }}>
          <Field label="Phase ID"><input style={inputStyle} value={draft.id} onChange={e => setDraft({ ...draft, id: e.target.value })} /></Field>
          <Field label="Phase Name"><input style={inputStyle} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Field label="Start Month"><input type="number" style={inputStyle} value={draft.start_month} onChange={e => setDraft({ ...draft, start_month: +e.target.value })} /></Field>
            <Field label="End Month"><input type="number" style={inputStyle} value={draft.end_month} onChange={e => setDraft({ ...draft, end_month: +e.target.value })} /></Field>
          </div>
          <Field label="Status">
            <select style={selectStyle} value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
              {["Not Started","In Progress","Complete","At Risk"].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Color"><input type="color" value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} style={{ ...inputStyle, height: "40px", padding: "4px 8px" }} /></Field>
          <Field label="Objectives (one per line)"><textarea style={textareaStyle} value={typeof draft.objectives === "string" ? draft.objectives : (draft.objectives || []).join("\n")} onChange={e => setDraft({ ...draft, objectives: e.target.value })} /></Field>
          <Field label="Deliverables (one per line)"><textarea style={textareaStyle} value={typeof draft.deliverables === "string" ? draft.deliverables : (draft.deliverables || []).join("\n")} onChange={e => setDraft({ ...draft, deliverables: e.target.value })} /></Field>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setEditTarget(null); setAddOpen(false); }}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Section: Milestones ──────────────────────────────────────────────────────
function MilestonesSection({ milestones, phases, onChange }) {
  const [editTarget, setEditTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const blank = { id: "", phase_id: phases[0]?.id || "P1", name: "", target_week: 1, status: "Pending" };
  const [draft, setDraft] = useState(blank);

  const save = () => {
    if (editTarget) onChange(milestones.map(m => m.id === editTarget.id ? { ...draft } : m));
    else onChange([...milestones, { ...draft, id: "M" + uid() }]);
    setEditTarget(null); setAddOpen(false); setDraft(blank);
  };
  const del = id => onChange(milestones.filter(m => m.id !== id));
  const cycleStatus = m => {
    const cycle = ["Pending", "In Progress", "Done", "At Risk"];
    const next = cycle[(cycle.indexOf(m.status) + 1) % cycle.length];
    onChange(milestones.map(x => x.id === m.id ? { ...x, status: next } : x));
  };

  const grouped = phases.reduce((acc, p) => {
    acc[p.id] = milestones.filter(m => m.phase_id === p.id);
    return acc;
  }, {});

  return (
    <div>
      <SectionHeader title="Milestones" sub={`${milestones.length} milestones across ${phases.length} phases`} action={<Btn onClick={() => { setDraft(blank); setAddOpen(true); }}>+ Add Milestone</Btn>} />
      {phases.map(p => (
        <div key={p.id} style={{ marginBottom: "20px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: "15px", color: p.color || "#2B5C8A", marginBottom: "8px", paddingLeft: "4px", borderLeft: `3px solid ${p.color || "#2B5C8A"}`, paddingLeft: "10px" }}>
            {p.id} — {p.name}
          </div>
          <div style={{ border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px", overflow: "hidden" }}>
            {(grouped[p.id] || []).length === 0 ? (
              <div style={{ padding: "14px 16px", fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#6B6B6B" }}>No milestones defined.</div>
            ) : (grouped[p.id] || []).map((m, i) => (
              <div key={m.id} style={{
                display: "flex", alignItems: "center", gap: "12px",
                padding: "12px 16px", borderBottom: i < grouped[p.id].length - 1 ? "1px solid rgba(26,26,26,0.08)" : "none",
                background: i % 2 === 0 ? "#F7F5F0" : "#FDFCF9",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "15px" }}>{m.name}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", marginTop: "2px" }}>Week {m.target_week}</div>
                </div>
                <button onClick={() => cycleStatus(m)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <Badge label={m.status} color={STATUS_COLORS[m.status]} />
                </button>
                <button onClick={() => { setDraft({ ...m }); setEditTarget(m); }} style={{ background: "none", border: "1px solid rgba(43,92,138,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 10px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#2B5C8A" }}>Edit</button>
                <button onClick={() => del(m.id)} style={{ background: "none", border: "1px solid rgba(139,0,0,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 10px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#8B0000" }}>×</button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {(editTarget || addOpen) && (
        <Modal title={editTarget ? "Edit Milestone" : "Add Milestone"} onClose={() => { setEditTarget(null); setAddOpen(false); }}>
          <Field label="Milestone Name"><input style={inputStyle} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></Field>
          <Field label="Phase">
            <select style={selectStyle} value={draft.phase_id} onChange={e => setDraft({ ...draft, phase_id: e.target.value })}>
              {phases.map(p => <option key={p.id} value={p.id}>{p.id} — {p.name}</option>)}
            </select>
          </Field>
          <Field label="Target Week"><input type="number" style={inputStyle} value={draft.target_week} onChange={e => setDraft({ ...draft, target_week: +e.target.value })} /></Field>
          <Field label="Status">
            <select style={selectStyle} value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
              {["Pending","In Progress","Done","At Risk"].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setEditTarget(null); setAddOpen(false); }}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Section: Resources ───────────────────────────────────────────────────────
function ResourcesSection({ resources, phases, onChange }) {
  const [editTarget, setEditTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const blank = { id: "", role: "", count: 1, phases: [], allocation: 100 };
  const [draft, setDraft] = useState(blank);

  const save = () => {
    if (editTarget) onChange(resources.map(r => r.id === editTarget.id ? { ...draft } : r));
    else onChange([...resources, { ...draft, id: "R" + uid() }]);
    setEditTarget(null); setAddOpen(false); setDraft(blank);
  };
  const del = id => onChange(resources.filter(r => r.id !== id));
  const total = resources.reduce((s, r) => s + r.count, 0);

  return (
    <div>
      <SectionHeader title="Resource Plan" sub={`${resources.length} roles · ${total} total headcount`} action={<Btn onClick={() => { setDraft(blank); setAddOpen(true); }}>+ Add Role</Btn>} />
      <div style={{ border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr auto", background: "#EFECE5", padding: "10px 16px", gap: "12px" }}>
          {["Role", "Count", "Phases", "Allocation", ""].map(h => (
            <div key={h} style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#6B6B6B" }}>{h}</div>
          ))}
        </div>
        {resources.map((r, i) => (
          <div key={r.id} style={{
            display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr auto",
            padding: "12px 16px", gap: "12px", alignItems: "center",
            borderTop: "1px solid rgba(26,26,26,0.08)",
            background: i % 2 === 0 ? "#F7F5F0" : "#FDFCF9",
          }}>
            <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "15px" }}>{r.role}</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "22px", color: "#2B5C8A", lineHeight: 1 }}>{r.count}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {(r.phases || []).map(pid => {
                const ph = phases.find(p => p.id === pid);
                return <Badge key={pid} label={pid} color={ph?.color || "#2B5C8A"} />;
              })}
            </div>
            <div>
              <div style={{ height: "4px", background: "#EFECE5", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ width: `${r.allocation}%`, height: "100%", background: "#2B5C8A" }} />
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", marginTop: "3px" }}>{r.allocation}%</div>
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <button onClick={() => { setDraft({ ...r }); setEditTarget(r); }} style={{ background: "none", border: "1px solid rgba(43,92,138,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 10px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#2B5C8A" }}>Edit</button>
              <button onClick={() => del(r.id)} style={{ background: "none", border: "1px solid rgba(139,0,0,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 10px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#8B0000" }}>×</button>
            </div>
          </div>
        ))}
      </div>
      {(editTarget || addOpen) && (
        <Modal title={editTarget ? "Edit Role" : "Add Role"} onClose={() => { setEditTarget(null); setAddOpen(false); }}>
          <Field label="Role Title"><input style={inputStyle} value={draft.role} onChange={e => setDraft({ ...draft, role: e.target.value })} /></Field>
          <Field label="Headcount"><input type="number" style={inputStyle} value={draft.count} onChange={e => setDraft({ ...draft, count: +e.target.value })} /></Field>
          <Field label="Allocation (%)"><input type="number" style={inputStyle} value={draft.allocation} min={0} max={100} onChange={e => setDraft({ ...draft, allocation: +e.target.value })} /></Field>
          <Field label="Phases (select all that apply)">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {phases.map(p => (
                <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "11px" }}>
                  <input type="checkbox" checked={(draft.phases || []).includes(p.id)} onChange={e => {
                    const cur = draft.phases || [];
                    setDraft({ ...draft, phases: e.target.checked ? [...cur, p.id] : cur.filter(x => x !== p.id) });
                  }} />
                  {p.id}
                </label>
              ))}
            </div>
          </Field>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setEditTarget(null); setAddOpen(false); }}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Section: RAID Log ────────────────────────────────────────────────────────
function RaidSection({ items, onChange }) {
  const [editTarget, setEditTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [filter, setFilter] = useState("All");
  const blank = { id: "", type: "Risk", category: "", description: "", severity: "Medium", status: "Open", mitigation: "" };
  const [draft, setDraft] = useState(blank);

  const save = () => {
    if (editTarget) onChange(items.map(x => x.id === editTarget.id ? { ...draft } : x));
    else onChange([...items, { ...draft, id: "K" + uid() }]);
    setEditTarget(null); setAddOpen(false); setDraft(blank);
  };
  const del = id => onChange(items.filter(x => x.id !== id));
  const cycleStatus = item => {
    const cycle = ["Open","Mitigated","Closed"];
    const next = cycle[(cycle.indexOf(item.status) + 1) % cycle.length];
    onChange(items.map(x => x.id === item.id ? { ...x, status: next } : x));
  };

  const types = ["All", "Risk", "Assumption", "Issue", "Dependency"];
  const filtered = filter === "All" ? items : items.filter(x => x.type === filter);

  return (
    <div>
      <SectionHeader title="RAID Log" sub={`${items.length} items`} action={<Btn onClick={() => { setDraft(blank); setAddOpen(true); }}>+ Add Item</Btn>} />
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
        {types.map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{
            padding: "6px 14px", fontFamily: "'DM Mono', monospace", fontSize: "11px",
            letterSpacing: "0.04em", cursor: "pointer", borderRadius: "2px",
            border: filter === t ? "none" : "1px solid rgba(26,26,26,0.2)",
            background: filter === t ? "#2B5C8A" : "transparent",
            color: filter === t ? "#F7F5F0" : "#6B6B6B",
          }}>{t} {filter === t ? `(${filtered.length})` : ""}</button>
        ))}
      </div>
      <div style={{ display: "grid", gap: "8px" }}>
        {filtered.map(item => (
          <div key={item.id} style={{
            border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px",
            background: "#F7F5F0", padding: "14px 16px",
            borderLeft: `3px solid ${SEVERITY_COLORS[item.severity] || "#aaa"}`,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "6px", alignItems: "center" }}>
                  <Badge label={item.type} color="#2B5C8A" />
                  <Badge label={item.severity} color={SEVERITY_COLORS[item.severity]} />
                  <Badge label={item.category} color="#6B6B6B" />
                </div>
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "15px", marginBottom: "4px" }}>{item.description}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#6B6B6B" }}>
                  <span style={{ color: "#7A5C1E" }}>Mitigation: </span>{item.mitigation}
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <button onClick={() => cycleStatus(item)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <Badge label={item.status} color={STATUS_COLORS[item.status]} />
                </button>
                <button onClick={() => { setDraft({ ...item }); setEditTarget(item); }} style={{ background: "none", border: "1px solid rgba(43,92,138,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 10px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#2B5C8A" }}>Edit</button>
                <button onClick={() => del(item.id)} style={{ background: "none", border: "1px solid rgba(139,0,0,0.3)", borderRadius: "2px", cursor: "pointer", padding: "4px 8px", fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#8B0000" }}>×</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {(editTarget || addOpen) && (
        <Modal title={editTarget ? "Edit RAID Item" : "Add RAID Item"} onClose={() => { setEditTarget(null); setAddOpen(false); }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Field label="Type">
              <select style={selectStyle} value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}>
                {["Risk","Assumption","Issue","Dependency"].map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Severity">
              <select style={selectStyle} value={draft.severity} onChange={e => setDraft({ ...draft, severity: e.target.value })}>
                {["High","Medium","Low"].map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Category"><input style={inputStyle} value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} /></Field>
          <Field label="Description"><textarea style={textareaStyle} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} /></Field>
          <Field label="Mitigation"><textarea style={textareaStyle} value={draft.mitigation} onChange={e => setDraft({ ...draft, mitigation: e.target.value })} /></Field>
          <Field label="Status">
            <select style={selectStyle} value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>
              {["Open","Mitigated","Closed"].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setEditTarget(null); setAddOpen(false); }}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Section: Architecture ────────────────────────────────────────────────────
function ArchitectureSection() {
  const layers = [
    { name: "Oracle Fusion Cloud", sub: "Source System of Record", color: "#2B5C8A", icon: "⬡" },
    { name: "Orbit Analytics ETL", sub: "Extraction · Transformation · Load", color: "#1a7a4a", icon: "⇅" },
    { name: "Bronze Layer", sub: "Raw ingestion · Landing zone · CDC", color: "#7A5C1E", icon: "◈" },
    { name: "Silver Layer", sub: "Curated · Standardized · Quality-checked", color: "#7A5C1E", icon: "◈" },
    { name: "Gold Layer", sub: "Enterprise DW · Semantic model · KPI catalog", color: "#6B2D8A", icon: "◈" },
    { name: "Consumption Layer", sub: "Power BI · Orbit Analytics · OTBI · BI Publisher", color: "#2B5C8A", icon: "▦" },
  ];
  return (
    <div>
      <SectionHeader title="Medallion Architecture" sub="Oracle Fusion → Enterprise DW → Reporting" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px" }}>
        {layers.map((l, i) => (
          <div key={i} style={{
            border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px",
            background: "#F7F5F0", padding: "16px 18px",
            borderLeft: `3px solid ${l.color}`,
            display: "flex", alignItems: "center", gap: "14px",
          }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "28px", color: l.color, lineHeight: 1 }}>{l.icon}</div>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "15px", marginBottom: "2px" }}>{l.name}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B" }}>{l.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Summary Dashboard ────────────────────────────────────────────────────────
function Dashboard({ program, phases, milestones, resources, risks }) {
  const totalFTE = resources.reduce((s, r) => s + r.count, 0);
  const openRisks = risks.filter(r => r.type === "Risk" && r.status === "Open").length;
  const highRisks = risks.filter(r => r.severity === "High" && r.status === "Open").length;
  const doneMS = milestones.filter(m => m.status === "Done").length;
  const stats = [
    { label: "Program Duration", value: `${program.duration_months}`, unit: "months" },
    { label: "Phases", value: phases.length, unit: "total" },
    { label: "Milestones", value: milestones.length, unit: `${doneMS} done` },
    { label: "Total FTE", value: totalFTE, unit: `${resources.length} roles` },
    { label: "Open Risks", value: openRisks, unit: `${highRisks} high` },
    { label: "Program Status", value: program.status, unit: program.methodology },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "1px", background: "rgba(26,26,26,0.1)", border: "1px solid rgba(26,26,26,0.13)", borderRadius: "3px", overflow: "hidden", marginBottom: "32px" }}>
      {stats.map((s, i) => (
        <div key={i} style={{ background: "#F7F5F0", padding: "18px 16px" }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#6B6B6B", marginBottom: "6px" }}>{s.label}</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "30px", color: "#2B5C8A", lineHeight: 1, marginBottom: "2px" }}>{s.value}</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B" }}>{s.unit}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [data, setData] = useState({
    program: SEED.program,
    phases: SEED.phases.map(p => ({ ...p, objectives: [...p.objectives], deliverables: [...p.deliverables] })),
    milestones: [...SEED.milestones],
    resources: [...SEED.resources],
    risks: [...SEED.risks],
  });

  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "phases", label: "Phases" },
    { id: "milestones", label: "Milestones" },
    { id: "resources", label: "Resources" },
    { id: "raid", label: "RAID Log" },
    { id: "architecture", label: "Architecture" },
    { id: "fin-reqs", label: "Financials Reqs", href: "https://fin.bicmm.com", icon: "↗" },
    { id: "scm-reqs", label: "Supply Chain Reqs", href: "https://scm.bicmm.com", icon: "↗" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#EFECE5", fontFamily: "'EB Garamond', serif" }}>
      {/* Header */}
      <header style={{
        background: "#0A0A0A", color: "#F7F5F0",
        borderBottom: "3px solid #2B5C8A",
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
          <div style={{ padding: "20px 0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            {/* Logo + title */}
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <a href="https://bicmm.com" target="_blank" rel="noopener noreferrer" title="bicmm.com" style={{ display: "flex", alignItems: "center", flexShrink: 0, opacity: 0.9 }}
                onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                onMouseLeave={e => e.currentTarget.style.opacity = "0.9"}
              >
                <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="14" cy="14" r="13" stroke="#4A80B5" strokeWidth="1.5"/>
                  <rect x="6" y="17" width="4" height="5" fill="#F7F5F0"/>
                  <rect x="12" y="13" width="4" height="9" fill="#F7F5F0"/>
                  <rect x="18" y="9" width="4" height="13" fill="#F7F5F0"/>
                </svg>
              </a>
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#4A80B5", marginBottom: "5px" }}>Enterprise Program Management</div>
                <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.01em", lineHeight: 1.1 }}>Oracle Fusion EDW Program</h1>
              </div>
            </div>
            {/* Right meta + sitemap */}
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <a href="https://sitemap.bicmm.com" target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", gap: "5px",
                fontFamily: "'DM Mono', monospace", fontSize: "10px",
                letterSpacing: "0.08em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)", textDecoration: "none",
                border: "1px solid rgba(74,128,181,0.3)", borderRadius: "2px",
                padding: "5px 10px", transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "#F7F5F0"; e.currentTarget.style.borderColor = "#4A80B5"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.35)"; e.currentTarget.style.borderColor = "rgba(74,128,181,0.3)"; }}
              >
                Sitemap <span style={{ fontSize: "9px" }}>↗</span>
              </a>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#4A80B5", textTransform: "uppercase", letterSpacing: "0.06em" }}>Source</div>
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "13px", color: "#F7F5F0" }}>{data.program.source_system}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#4A80B5", textTransform: "uppercase", letterSpacing: "0.06em" }}>Partner</div>
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: "13px", color: "#F7F5F0" }}>{data.program.etl_partner}</div>
              </div>
            </div>
          </div>
          {/* Nav */}
          <nav style={{ display: "flex", gap: "0", borderTop: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
            {tabs.map(t => t.href ? (
              <a key={t.id} href={t.href} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", alignItems: "center", gap: "5px",
                padding: "12px 18px", fontFamily: "'DM Mono', monospace",
                fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.4)", textDecoration: "none",
                borderBottom: "2px solid transparent",
                borderLeft: "1px solid rgba(255,255,255,0.08)",
                transition: "color 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = "#F7F5F0"; e.currentTarget.style.borderBottomColor = "#4A80B5"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; e.currentTarget.style.borderBottomColor = "transparent"; }}
              >
                {t.label}
                <span style={{ fontSize: "10px", opacity: 0.6 }}>{t.icon}</span>
              </a>
            ) : (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "12px 18px", fontFamily: "'DM Mono', monospace",
                fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase",
                color: activeTab === t.id ? "#F7F5F0" : "rgba(255,255,255,0.4)",
                borderBottom: activeTab === t.id ? "2px solid #2B5C8A" : "2px solid transparent",
                transition: "color 0.15s",
              }}>{t.label}</button>
            ))}
          </nav>
        </div>
      </header>

      {/* Body */}
      <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 24px" }}>
        {activeTab === "overview" && (
          <>
            <Dashboard {...data} />
            <ProgramSection data={data.program} onChange={program => setData({ ...data, program })} />
          </>
        )}
        {activeTab === "phases" && (
          <PhasesSection phases={data.phases} onChange={phases => setData({ ...data, phases })} />
        )}
        {activeTab === "milestones" && (
          <MilestonesSection milestones={data.milestones} phases={data.phases} onChange={milestones => setData({ ...data, milestones })} />
        )}
        {activeTab === "resources" && (
          <ResourcesSection resources={data.resources} phases={data.phases} onChange={resources => setData({ ...data, resources })} />
        )}
        {activeTab === "raid" && (
          <RaidSection items={data.risks} onChange={risks => setData({ ...data, risks })} />
        )}
        {activeTab === "architecture" && <ArchitectureSection />}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(26,26,26,0.13)", background: "#F7F5F0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
          {/* Logo wordmark */}
          <a href="https://bicmm.com" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: "9px", textDecoration: "none", opacity: 0.7 }}
            onMouseEnter={e => e.currentTarget.style.opacity = "1"}
            onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
          >
            <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="13" stroke="#2B5C8A" strokeWidth="1.5"/>
              <rect x="6" y="17" width="4" height="5" fill="#2B5C8A"/>
              <rect x="12" y="13" width="4" height="9" fill="#2B5C8A"/>
              <rect x="18" y="9" width="4" height="13" fill="#2B5C8A"/>
            </svg>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2B5C8A" }}>bicmm.com</span>
          </a>
          {/* Center copy */}
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#6B6B6B", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Oracle Fusion EDW Program · {data.program.delivery_model} · {data.program.methodology}
          </span>
          {/* Sitemap link */}
          <a href="https://sitemap.bicmm.com" target="_blank" rel="noopener noreferrer" style={{
            fontFamily: "'DM Mono', monospace", fontSize: "10px", letterSpacing: "0.07em",
            textTransform: "uppercase", color: "#2B5C8A", textDecoration: "none",
            display: "flex", alignItems: "center", gap: "4px", opacity: 0.6,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "1"}
          onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}
          >
            Sitemap <span style={{ fontSize: "9px" }}>↗</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
